package fr.labodoc.healthProfessional.webApp.pages.dashboard.components

import fr.labodoc.healthProfessional.webApp.Page
import fr.labodoc.healthProfessional.webApp.components.LabodocSelect
import fr.labodoc.healthProfessional.webApp.components.hr
import fr.labodoc.healthProfessional.webApp.components.labodocSpinner
import fr.labodoc.healthProfessional.webApp.components.navigoLink
import fr.labodoc.healthProfessional.webApp.utils.toggleCssClass
import fr.labodoc.require
import io.kvision.core.onChange
import io.kvision.core.onClick
import io.kvision.core.onEvent
import io.kvision.core.onInput
import io.kvision.form.check.checkBoxInput
import io.kvision.form.text.textInput
import io.kvision.html.*
import io.kvision.html.Header
import io.kvision.state.ObservableValue
import io.kvision.state.bind
import kotlinx.browser.document
import kotlinx.coroutines.flow.StateFlow
import org.w3c.dom.Node

class MedicineList(
  viewModel: MedicineListViewModel
) : Div(className = "medicine-list") {
  init {
    add(
      Header(
        onSearchBarInputUpdate = viewModel::updateSearchBarInput,
        searchBarResults = viewModel.searchBarResults,
        atcClassifications = viewModel.atcClassifications,
        onAtcClassificationSelect = viewModel::updateAtcClassification,
        interspecialityThematics = viewModel.interspecialityThematics,
        specialityThematics = viewModel.specialityThematics,
        onThematicSelect = viewModel::updateThematic,
        filter = viewModel.filter,
        onResetFilterClick = viewModel::resetFilter,
        alphabeticalSort = viewModel.alphabeticalSort,
        onAlphabeticalSortClick = viewModel::updateAlphabeticalSort,
        medicinesFilteredCount = viewModel.medicinesFilteredCount
      )
    )

    div(className = "container").bind(viewModel.medicineListState) { medicineListState ->
      when (medicineListState) {
        is MedicineListViewModel.MedicineListState.Loading -> {
          labodocSpinner()
        }

        is MedicineListViewModel.MedicineListState.Error -> {
          p("Une erreur est survenue")
        }

        is MedicineListViewModel.MedicineListState.Loaded -> {
          if (medicineListState.medicines.isEmpty()) {
            p("Aucun médicaments", className = "no-medicines")
          } else {
            div(className = "medicine-cards") {
              medicineListState.medicines
                .forEach { medicine ->
                  navigoLink("", Page.MedicineSheet(medicine.id)) {
                    add(
                      MedicineCard(
                        atcClassificationCategoryCode = medicine.atcClassificationCategory.code.first(),
                        atcClassificationCategoryName = medicine.atcClassificationCategory.name,
                        name = medicine.name,
                        logoUrl = medicine.logoUrl,
                        dci = medicine.dci,
                        laboratoryName = medicine.laboratory?.name,
                      )
                    )
                  }
                }
            }
          }
        }
      }
    }
  }
}

private class Header(
  onSearchBarInputUpdate: (String?) -> Unit,
  searchBarResults: StateFlow<Set<MedicineListViewModel.SearchBarResult>>,
  atcClassifications: StateFlow<List<MedicineListViewModel.AtcClassification>>,
  onAtcClassificationSelect: (String) -> Unit,
  interspecialityThematics: StateFlow<List<MedicineListViewModel.Thematic>>,
  specialityThematics: StateFlow<List<MedicineListViewModel.Thematic>>,
  onThematicSelect: (String?) -> Unit,
  onResetFilterClick: () -> Unit,
  filter: StateFlow<MedicineListViewModel.Filter?>,
  alphabeticalSort: StateFlow<Boolean>,
  onAlphabeticalSortClick: (Boolean) -> Unit,
  medicinesFilteredCount: StateFlow<Int?>
) : Header() {
  init {
    add(
      SearchBar(
        searchBarResults = searchBarResults,
        filter = filter,
        onSearchBarInputUpdate = onSearchBarInputUpdate
      )
    )

    div(className = "filter-options") {
      add(
        ThematicSelect(
          label = "Thématiques de ma spécialité",
          className = "thematics-speciality",
          thematics = specialityThematics,
          filter = filter,
          onThematicSelect = onThematicSelect
        )
      )

      add(
        ThematicSelect(
          label = "Thématiques inter spécialités",
          className = "thematics-interspeciality",
          thematics = interspecialityThematics,
          filter = filter,
          onThematicSelect = onThematicSelect
        )
      )

      add(
        AtcClassificationSelect(
          atcClassifications = atcClassifications,
          filter = filter,
          onAtcClassificationSelect = onAtcClassificationSelect
        )
      )
    }

    div(className = "sort-options") {
      div(className = "medicine-results").bind(medicinesFilteredCount) { medicinesFilteredCount ->
        if (medicinesFilteredCount != null)
          show()
        else
          hide()

        icon("fa-solid fa-circle-xmark") {
          onClick {
            onResetFilterClick()
          }
        }

        p(className = "medicines-number") {
          content = "$medicinesFilteredCount médicaments trouvés"
        }

        val explanationDisplayed = ObservableValue(false)
        icon("fa-solid fa-circle-info") {
          document.addEventListener("click", { event ->
            explanationDisplayed.value = if (event.target == this.getElement())
              !explanationDisplayed.value
            else
              false
          })
        }

        p(className = "explanation") {
          rich = true
          content = """
           <b>Avertissement</b> : malgré tout le soin apporté à l'élaboration de LaboDoc, une erreur ou un oubli sont possibles dans les informations diffusées. L'équipe LaboDoc remercie par avance les professionnels utilisateurs de bien vouloir les signaler via notre formulaire de contact et attire leur attention sur le fait qu'ils doivent conserver la plus grande vigilance dans l'exploitation des données mises à leur disposition.<br>Les liens vers les sites WEB de l'ANSM, de la Base de donnée publique des médicaments et de la HAS sont également à leur disposition pour les aider dans leurs recherches.
         """.trimIndent()
        }.bind(explanationDisplayed, removeChildren = false) { displayed ->
          if (displayed)
            show()
          else
            hide()
        }
      }

      add(
        AlphabeticalSortSwitch(
          alphabeticalSort = alphabeticalSort,
          onClick = onAlphabeticalSortClick
        )
      )
    }

    hr()
  }
}

private class SearchBar(
  searchBarResults: StateFlow<Set<MedicineListViewModel.SearchBarResult>>,
  filter: StateFlow<MedicineListViewModel.Filter?>,
  onSearchBarInputUpdate: (String?) -> Unit
) : Div(className = "search-bar") {
  init {
    div(className = "input") {
      icon("fa-solid fa-magnifying-glass")

      textInput {
        placeholder = "Chercher par nom ou DCI"

        onInput {
          onSearchBarInputUpdate(value)
        }
      }.bind(filter) { filter ->
        value = filter?.let {
          if (filter is MedicineListViewModel.Filter.Search) filter.input else null
        }
      }
    }

    div(className = "results").bind(searchBarResults) { searchBarResults ->
      ul {
        searchBarResults.forEach { searchBarResult ->
          li {
            rich = true
            content = """
              ${searchBarResult.text}${if (searchBarResult.isLaboratoryMedicine) "<sup>®</sup>" else ""}
            """.trimIndent()

            // On click won't work due to visibility
            onEvent {
              mousedown = {
                onSearchBarInputUpdate(searchBarResult.text)
              }
            }
          }
        }
      }
    }
  }
}

private class ThematicSelect(
  label: String,
  className: String,
  thematics: StateFlow<List<MedicineListViewModel.Thematic>>,
  filter: StateFlow<MedicineListViewModel.Filter?>,
  onThematicSelect: (String?) -> Unit
) : LabodocSelect(label = label, emptyOption = true) {
  init {
    addCssClass("thematic-select")
    addCssClass(className)
    onChange {
      onThematicSelect(value)
    }

    this.bind(thematics) { thematics ->
      options = thematics.map { thematic ->
        thematic.id to "${thematic.name} (${thematic.numberOfMedicines})"
      }
      value = null
    }.bind(filter) { filter ->
      value = filter?.let {
        if (filter is MedicineListViewModel.Filter.Thematic) filter.id else null
      }
    }
  }
}

private class AtcClassificationSelect(
  atcClassifications: StateFlow<List<MedicineListViewModel.AtcClassification>>,
  filter: StateFlow<MedicineListViewModel.Filter?>,
  onAtcClassificationSelect: (String) -> Unit
) : Div(className = "atc-classification-select") {
  init {
    onClick {
      toggleCssClass("open")
    }
    document.addEventListener("click", { event ->
      val target = event.target

      if (target is Node && this.getElement()?.contains(target) != true)
        removeCssClass("open")

    })

    this.bind(filter) { filter ->
      label("Recherche assistée")

      if (filter is MedicineListViewModel.Filter.AtcClassification) {
        addCssClass("selected")
        p("${filter.code} - ${filter.name} (${filter.numberOfMedicines})")
      } else {
        removeCssClass("selected")
      }

      div(className = "dropdown") {
        ul(className = "atc-classifications").bind(atcClassifications) { atcClassifications ->
          val atcClassificationsToDisplay = if (filter is MedicineListViewModel.Filter.AtcClassification) {
            li(className = "atc-classification current") {
              onClick {
                onAtcClassificationSelect(filter.code)
                it.stopPropagation()
              }

              rich = true
              content = """
                  <i class="fa-solid fa-arrow-turn-up"></i>${filter.code} - ${filter.name} (${filter.numberOfMedicines})
                """.trimIndent()
            }

            atcClassifications.filter { it.parentCode == filter.code }
          } else
            atcClassifications.filter { it.parentCode == null }

          atcClassificationsToDisplay.forEach { atcClassification ->
            li(
              "${atcClassification.code} - ${atcClassification.name} (${atcClassification.numberOfMedicines})",
              className = "atc-classification"
            ) {
              onClick {
                onAtcClassificationSelect(atcClassification.code)
                it.stopPropagation()
              }
            }
          }
        }
      }
    }
  }
}

private class AlphabeticalSortSwitch(
  alphabeticalSort: StateFlow<Boolean>,
  onClick: (Boolean) -> Unit
) : Label(className = "alphabetical-sort-switch") {
  init {
    checkBoxInput {
      onClick {
        onClick(value)
      }
    }.bind(alphabeticalSort) {
      value = it
    }

    div {
      span(className = "ball")
      image(require("./img/sort-icon.svg"))
    }
  }
}

private class MedicineCard(
  atcClassificationCategoryCode: Char,
  atcClassificationCategoryName: String,
  name: String,
  logoUrl: String,
  dci: String,
  laboratoryName: String?
) : Div(className = "medicine-card") {
  init {
    p(atcClassificationCategoryName, className = "atc-classification-category $atcClassificationCategoryCode")

    if (laboratoryName != null && logoUrl.contains("placeholder"))
      p(rich = true, className = "name") {
        content = "${name}<sup>®</sup>"
      }
    else
      image(logoUrl, alt = name, className = "logo")

    hr()

    p(laboratoryName?.let { dci } ?: name.lowercase(), className = "dci")

    p(laboratoryName ?: "Groupe générique", className = "laboratory")
  }
}
